import { useEffect, useMemo, useState } from 'react';
import { App } from 'antd';
import WorkSpaceService from 'services/master/workspace';

const services = new WorkSpaceService();

const useComponent = () => {
  const { message } = App.useApp();
  const [data, setData] = useState([]);
  const [keyword, setKeyword] = useState('');
  const [packages, setPackages] = useState([]);
  const [packageFilter, setPackageFilter] = useState('all');
  const [expiryFilter, setExpiryFilter] = useState('all');
  const [renewTarget, setRenewTarget] = useState(null);
  const [renewPackageKey, setRenewPackageKey] = useState('mini');
  const [renewDuration, setRenewDuration] = useState(1);
  const [renewSubmitting, setRenewSubmitting] = useState(false);

  const load = async () => {
    const [list, pkgList] = await Promise.all([
      services.list(),
      services.packageList(),
    ]);

    const sorted = Array.isArray(list)
      ? [...list].sort((a, b) => new Date(b?.create_on || 0) - new Date(a?.create_on || 0))
      : [];

    setData(sorted);
    setPackages(Array.isArray(pkgList) ? pkgList : []);
  };

  useEffect(() => {
    load();
  }, []);

  const filteredData = useMemo(() => {
    const normalizedKeyword = String(keyword || '').trim().toLowerCase();
    const now = new Date();

    let list = data;

    if (normalizedKeyword) {
      list = list.filter((record) => {
        const values = [
          record?.web_site?.title,
          record?.web_site?.url,
          record?.owner?.full_name,
          record?.owner?.phone,
        ]
          .filter(Boolean)
          .map((value) => String(value).toLowerCase());

        return values.some((value) => value.includes(normalizedKeyword));
      });
    }

    if (expiryFilter === 'expiring_7d') {
      list = list.filter((record) => {
        const expiryOn = record?.subscription?.expiry_on;
        if (!expiryOn) return false;
        const diffMs = new Date(expiryOn).getTime() - now.getTime();
        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
        return diffDays >= 0 && diffDays <= 7;
      });
    }

    if (packageFilter !== 'all') {
      list = list.filter((record) => {
        const tier = record?.subscription?.tier || 'mien_phi';
        return tier === packageFilter;
      });
    }

    return list;
  }, [data, keyword, expiryFilter, packageFilter]);

  const packageMap = useMemo(() => {
    return (packages || []).reduce((acc, item) => {
      acc[item?.key] = item;
      return acc;
    }, {});
  }, [packages]);

  const getPackageName = (record) => {
    const tier = record?.subscription?.tier || 'mien_phi';
    return packageMap[tier]?.name || tier;
  };

  const openRenewModal = (record) => {
    setRenewTarget(record);
    setRenewPackageKey(record?.subscription?.tier || 'mini');
    setRenewDuration(1);
  };

  const closeRenewModal = () => {
    if (renewSubmitting) return;
    setRenewTarget(null);
  };

  const submitRenew = async () => {
    if (!renewTarget?.id) return;
    setRenewSubmitting(true);
    try {
      await services.renew({
        w_id: renewTarget.id,
        package_key: renewPackageKey,
        duration_months: renewDuration,
      });
      message.success('Gia hạn gói cước thành công');
      setRenewTarget(null);
      await load();
    } catch (error) {
      message.error(error?.message || 'Gia hạn thất bại');
    } finally {
      setRenewSubmitting(false);
    }
  };

  const onExpiryFilterChange = (value) => {
    setExpiryFilter(value || 'all');
  };

  const onPackageFilterChange = (value) => {
    setPackageFilter(value || 'all');
  };

  const onKeywordChange = (event) => {
    setKeyword(event?.target?.value || '');
  };

  return {
    data,
    filteredData,
    keyword,
    packages,
    packageFilter,
    expiryFilter,
    renewTarget,
    renewPackageKey,
    renewDuration,
    renewSubmitting,
    getPackageName,
    openRenewModal,
    closeRenewModal,
    submitRenew,
    setRenewPackageKey,
    setRenewDuration,
    onKeywordChange,
    onPackageFilterChange,
    onExpiryFilterChange,
  };
};

export default useComponent;